import { useState, useEffect } from 'react';
import { getMortgage, createMortgage, updateMortgage, addMortgageRate, deleteMortgageRate } from '../api';
import { styles } from '../styles';
import { Spinner } from '../components/Icons';

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

function computeMonthlyPayment(principal, annualRate, termMonths) {
  if (!principal || !annualRate || !termMonths) return null;
  const r = annualRate / 12;
  const n = termMonths;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MortgageSetupScreen({ onBack, onSave }) {
  const [existing, setExisting] = useState(null);
  const [ratePeriods, setRatePeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Add renewal form state
  const [showAddRate, setShowAddRate] = useState(false);
  const [addingRate, setAddingRate] = useState(false);
  const [rateForm, setRateForm] = useState({
    effective_date: '',
    annual_rate_pct: '',
    rate_type: 'fixed',
    term_years: '',
    notes: '',
  });
  const [rateError, setRateError] = useState(null);
  const [deletingRate, setDeletingRate] = useState(null);

  const [form, setForm] = useState({
    name: 'Primary Mortgage',
    original_principal: '',
    annual_rate_pct: '',   // user types "5.25", we store as "0.0525"
    term_years: '',
    initial_rate_term_years: '5',  // initial rate term
    start_date: '',
  });

  const loadData = () => {
    getMortgage()
      .then(d => {
        setExisting(d.mortgage);
        setRatePeriods(d.rate_periods || []);
        setForm({
          name: d.mortgage.name,
          original_principal: String(d.mortgage.original_principal),
          annual_rate_pct: String((Number(d.mortgage.annual_rate) * 100).toFixed(3)).replace(/\.?0+$/, ''),
          term_years: String(d.mortgage.term_months / 12),
          initial_rate_term_years: '5',
          start_date: d.mortgage.start_date,
        });
        setLoading(false);
      })
      .catch(err => {
        if (err.status === 404) {
          setExisting(null);
          const today = new Date().toISOString().split('T')[0];
          setForm(f => ({ ...f, start_date: today }));
        }
        setLoading(false);
      });
  };

  useEffect(() => { loadData(); }, []);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setRate = (field, value) => setRateForm(f => ({ ...f, [field]: value }));

  const principal = parseFloat(form.original_principal) || 0;
  const rate = parseFloat(form.annual_rate_pct) / 100 || 0;
  const termMonths = Math.round((parseFloat(form.term_years) || 0) * 12);
  const previewPayment = computeMonthlyPayment(principal, rate, termMonths);

  const handleSave = async () => {
    setError(null);
    if (!principal || !rate || !termMonths || !form.start_date) {
      setError('Please fill in all fields');
      return;
    }
    if (rate < 0.001 || rate > 0.3) {
      setError('Interest rate should be between 0.1% and 30%');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        original_principal: principal,
        annual_rate: rate,
        term_months: termMonths,
        start_date: form.start_date,
        initial_rate_term_months: Math.round((parseFloat(form.initial_rate_term_years) || 5) * 12),
      };
      if (existing) {
        await updateMortgage({
          name: payload.name,
          annual_rate: payload.annual_rate,
          term_months: payload.term_months,
          start_date: payload.start_date,
        });
      } else {
        await createMortgage(payload);
      }
      onSave();
    } catch (e) {
      setError(e.message || 'Failed to save mortgage');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRate = async () => {
    setRateError(null);
    const newRate = parseFloat(rateForm.annual_rate_pct) / 100;
    const termMo = Math.round((parseFloat(rateForm.term_years) || 0) * 12);
    if (!rateForm.effective_date || !newRate || !termMo) {
      setRateError('Fill in all rate fields');
      return;
    }
    if (newRate < 0.001 || newRate > 0.3) {
      setRateError('Rate should be between 0.1% and 30%');
      return;
    }
    setAddingRate(true);
    try {
      await addMortgageRate({
        effective_date: rateForm.effective_date,
        annual_rate: newRate,
        rate_type: rateForm.rate_type,
        term_months: termMo,
        notes: rateForm.notes || null,
      });
      setShowAddRate(false);
      setRateForm({ effective_date: '', annual_rate_pct: '', rate_type: 'fixed', term_years: '', notes: '' });
      loadData();
    } catch (e) {
      setRateError(e.message || 'Failed to add rate period');
    } finally {
      setAddingRate(false);
    }
  };

  const handleDeleteRate = async (id) => {
    if (!window.confirm('Delete this rate period?')) return;
    setDeletingRate(id);
    try {
      await deleteMortgageRate(id);
      loadData();
    } catch (e) {
      alert(e.message || 'Failed to delete rate period');
    } finally {
      setDeletingRate(null);
    }
  };

  if (loading) {
    return (
      <div style={{ ...styles.screen, ...styles.centered }}>
        <Spinner />
      </div>
    );
  }

  const sortedPeriods = [...ratePeriods].sort((a, b) => a.effective_date.localeCompare(b.effective_date));

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}><BackIcon /></button>
        <h1 style={styles.headerTitle}>{existing ? 'Mortgage Settings' : 'Set Up Mortgage'}</h1>
        <div style={{ width: 40 }} />
      </header>

      {existing && (
        <div style={setupStyles.note}>
          Note: updating settings recalculates the monthly payment but does not modify past payments.
        </div>
      )}

      <div style={setupStyles.formCard}>
        <label style={setupStyles.label}>Loan Name</label>
        <input
          style={setupStyles.input}
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder="Primary Mortgage"
        />

        <label style={setupStyles.label}>Original Principal ($)</label>
        <input
          style={setupStyles.input}
          type="number"
          inputMode="decimal"
          value={form.original_principal}
          onChange={e => set('original_principal', e.target.value)}
          placeholder="450000"
          disabled={!!existing}
        />
        {existing && (
          <p style={setupStyles.hint}>Original principal cannot be changed after setup.</p>
        )}

        <label style={setupStyles.label}>Annual Interest Rate (%)</label>
        <input
          style={setupStyles.input}
          type="number"
          inputMode="decimal"
          step="0.001"
          value={form.annual_rate_pct}
          onChange={e => set('annual_rate_pct', e.target.value)}
          placeholder="5.25"
        />

        <label style={setupStyles.label}>Amortization Term (years)</label>
        <input
          style={setupStyles.input}
          type="number"
          inputMode="numeric"
          value={form.term_years}
          onChange={e => set('term_years', e.target.value)}
          placeholder="25"
        />

        {!existing && (
          <>
            <label style={setupStyles.label}>Initial Rate Term (years)</label>
            <input
              style={setupStyles.input}
              type="number"
              inputMode="numeric"
              value={form.initial_rate_term_years}
              onChange={e => set('initial_rate_term_years', e.target.value)}
              placeholder="5"
            />
            <p style={setupStyles.hint}>How long this rate is fixed for (e.g. 5 for a 5-year term).</p>
          </>
        )}

        <label style={setupStyles.label}>First Payment Date</label>
        <input
          style={setupStyles.input}
          type="date"
          value={form.start_date}
          onChange={e => set('start_date', e.target.value)}
        />

        {previewPayment !== null && termMonths > 0 && principal > 0 && (
          <div style={setupStyles.preview}>
            <span style={setupStyles.previewLabel}>Calculated Monthly Payment</span>
            <span style={setupStyles.previewValue}>
              ${previewPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
      </div>

      {error && <p style={setupStyles.error}>{error}</p>}

      <button style={setupStyles.saveBtn} onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : existing ? 'Update Mortgage' : 'Create Mortgage'}
      </button>

      {/* Rate History — only shown when a mortgage exists */}
      {existing && (
        <div style={{ marginTop: 24 }}>
          <div style={setupStyles.rateHeader}>
            <h3 style={setupStyles.sectionTitle}>Rate History</h3>
            {!showAddRate && (
              <button style={setupStyles.addRateBtn} onClick={() => setShowAddRate(true)}>
                + Add Renewal
              </button>
            )}
          </div>

          {sortedPeriods.map(p => (
            <div key={p.id} style={setupStyles.rateRow}>
              <div style={setupStyles.rateLeft}>
                <span style={setupStyles.rateDate}>{fmtDate(p.effective_date)}</span>
                <span style={setupStyles.rateRate}>
                  {(Number(p.annual_rate) * 100).toFixed(3).replace(/\.?0+$/, '')}%
                  {' '}
                  <span style={{
                    ...setupStyles.rateBadge,
                    backgroundColor: p.rate_type === 'fixed' ? '#dbeafe' : '#fef3c7',
                    color: p.rate_type === 'fixed' ? '#1e40af' : '#92400e',
                  }}>
                    {p.rate_type === 'fixed' ? 'FIXED' : 'VAR'}
                  </span>
                </span>
                <span style={setupStyles.rateTerm}>{p.term_months / 12}-year term</span>
                {p.notes && <span style={setupStyles.rateNotes}>{p.notes}</span>}
              </div>
              <button
                style={{
                  ...setupStyles.deleteRateBtn,
                  opacity: sortedPeriods.length <= 1 ? 0.3 : 1,
                  cursor: sortedPeriods.length <= 1 ? 'not-allowed' : 'pointer',
                }}
                disabled={sortedPeriods.length <= 1 || deletingRate === p.id}
                onClick={() => handleDeleteRate(p.id)}
              >
                ✕
              </button>
            </div>
          ))}

          {showAddRate && (
            <div style={setupStyles.addRateCard}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#1e293b' }}>Add Renewal Period</h4>

              <label style={setupStyles.label}>Effective Date (first payment at new rate)</label>
              <input
                style={setupStyles.input}
                type="date"
                value={rateForm.effective_date}
                onChange={e => setRate('effective_date', e.target.value)}
              />

              <label style={setupStyles.label}>New Annual Rate (%)</label>
              <input
                style={setupStyles.input}
                type="number"
                inputMode="decimal"
                step="0.001"
                value={rateForm.annual_rate_pct}
                onChange={e => setRate('annual_rate_pct', e.target.value)}
                placeholder="5.50"
              />

              <label style={setupStyles.label}>Rate Type</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                {['fixed', 'variable'].map(type => (
                  <button
                    key={type}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 8,
                      border: '1px solid',
                      borderColor: rateForm.rate_type === type ? '#6366f1' : '#e2e8f0',
                      backgroundColor: rateForm.rate_type === type ? '#ede9fe' : 'white',
                      color: rateForm.rate_type === type ? '#4f46e5' : '#64748b',
                      fontWeight: rateForm.rate_type === type ? 600 : 400,
                      fontSize: 13,
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                    }}
                    onClick={() => setRate('rate_type', type)}
                  >
                    {type}
                  </button>
                ))}
              </div>

              <label style={setupStyles.label}>Term Length (years)</label>
              <input
                style={setupStyles.input}
                type="number"
                inputMode="numeric"
                value={rateForm.term_years}
                onChange={e => setRate('term_years', e.target.value)}
                placeholder="5"
              />

              <label style={setupStyles.label}>Notes (optional)</label>
              <input
                style={setupStyles.input}
                value={rateForm.notes}
                onChange={e => setRate('notes', e.target.value)}
                placeholder="e.g. Renewed at TD Bank"
              />

              {rateError && <p style={setupStyles.error}>{rateError}</p>}

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  style={{ ...setupStyles.saveBtn, flex: 1, padding: '10px 0', fontSize: 14 }}
                  onClick={handleAddRate}
                  disabled={addingRate}
                >
                  {addingRate ? 'Saving…' : 'Save Renewal'}
                </button>
                <button
                  style={{ ...setupStyles.cancelBtn, flex: 1 }}
                  onClick={() => { setShowAddRate(false); setRateError(null); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const setupStyles = {
  note: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 13,
    color: '#92400e',
    marginBottom: 16,
  },
  formCard: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: '20px 20px',
    border: '1px solid #e2e8f0',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#475569',
    marginTop: 12,
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    fontSize: 15,
    backgroundColor: '#f8fafc',
    boxSizing: 'border-box',
    color: '#1e293b',
  },
  hint: {
    fontSize: 11,
    color: '#94a3b8',
    margin: '2px 0 0 0',
  },
  preview: {
    marginTop: 16,
    backgroundColor: '#f0f9ff',
    borderRadius: 10,
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previewLabel: {
    fontSize: 13,
    color: '#0369a1',
    fontWeight: 500,
  },
  previewValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#0369a1',
  },
  error: {
    color: '#ef4444',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 8,
  },
  saveBtn: {
    width: '100%',
    padding: '14px 0',
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '10px 0',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  // Rate History styles
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#475569',
    margin: 0,
  },
  rateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  addRateBtn: {
    fontSize: 13,
    fontWeight: 600,
    color: '#6366f1',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  rateRow: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    marginBottom: 8,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  rateLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  rateDate: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1e293b',
  },
  rateRate: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  rateBadge: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 4,
    letterSpacing: '0.05em',
  },
  rateTerm: {
    fontSize: 12,
    color: '#64748b',
  },
  rateNotes: {
    fontSize: 11,
    color: '#94a3b8',
    fontStyle: 'italic',
  },
  deleteRateBtn: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    fontSize: 13,
    padding: '2px 4px',
    marginTop: 2,
  },
  addRateCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 14,
    padding: '16px 16px',
    border: '1px solid #e2e8f0',
    marginTop: 8,
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
};
